import { YesNoAnswer } from 'scenes/common/types';
import { WireNameValue } from 'scenes/common/api/quote/types';
import {
  VehicleManuallyEntered,
  VehicleFromSearch,
  MotorFunnelState,
  MarketAgreedValue,
  VehicleSearchType,
  VehicleManualQuoteDetailsField,
} from 'scenes/motor/types';
import {
  getBusinessType,
  isVehicleUsedForBusiness,
  hasPreExistingDamage,
  getSelectedSpecialFeatures,
  hasSpecialFeatures,
  getVehicleModifications,
  getStereoCost,
  getLicencePlateNumberIfNotLookedUp,
  hasModifications,
} from 'scenes/motor/selectors';
import {
  defaultVehicleFromSearch,
  defaultVehicleManualSelectFields,
} from 'scenes/motor/flows/quoteDetails/defaults';
import { ADDRESS_ID, VEHICLE_ID } from 'scenes/common/api/quote/marshaller/ids';

import { removeDoubleSpace } from 'scenes/common/helpers/string';
import {
  WireVehicleRequest,
  WireMotorQuotePayload,
  WireVehicleValueType,
  VehicleProviderType,
  WireVehicleResponse,
  OtherAttributes,
  preExistingDamages,
} from '../types';
import { vehicleRefPartyIds } from './party';
import { wireVehicleUnderFinanceAttributes } from './finance';

const wireVehicleBusinessAttributes = (
  state: MotorFunnelState
): WireNameValue[] =>
  isVehicleUsedForBusiness(state)
    ? [
        {
          name: OtherAttributes.IS_USED_FOR_BUSINESS,
          value: 'true',
          description: getBusinessType(state),
        },
      ]
    : [{ name: OtherAttributes.IS_USED_FOR_BUSINESS, value: 'false' }];

const wireVehiclePreExistingDamageAttributes = (
  state: MotorFunnelState
): WireNameValue[] =>
  preExistingDamages.map((name) => ({
    name,
    value: hasPreExistingDamage(state) ? 'true' : 'false',
  }));

const wireVehicleSpecialFeaturesAttributes = (
  state: MotorFunnelState
): WireNameValue[] =>
  hasSpecialFeatures(state)
    ? [
        {
          name: OtherAttributes.NON_STANDARD,
          value: 'true',
          description: removeDoubleSpace(
            getSelectedSpecialFeatures(state).trim()
          ),
        },
      ]
    : [];

const wireVehicleModifications = (state: MotorFunnelState): WireNameValue[] =>
  hasModifications(state)
    ? getVehicleModifications(state).map((modification) => ({
        name: modification.name,
        value:
          modification.name === OtherAttributes.VEHICLE_MODIFICATION_STEREO
            ? getStereoCost(state)
            : 'true',
      }))
    : [];

const wireOtherAttributesFromVehicleDetails = ({
  transmissionType,
  variant,
  engine,
  driveType,
  family,
  bodyType,
}: VehicleManuallyEntered) => [
  { name: 'Transmission', value: transmissionType },
  { name: 'Variant', value: variant },
  { name: 'VehicleCylinder', value: engine.cylinders },
  { name: 'ManufacturerCode', value: family },
  { name: 'ModelCode', value: driveType },
  { name: 'VehTypeCode', value: bodyType },
  {
    name: 'Displacement',
    value: `${engine.capacity.value}${
      engine.capacity.unit?.toUpperCase() ?? ''
    }`,
  },
  { name: 'EngineTypeCode', value: engine.description },
];

const wireVehicleOtherAttributes = (
  state: MotorFunnelState,
  vehicleDetails: VehicleManuallyEntered
) => [
  ...wireOtherAttributesFromVehicleDetails(vehicleDetails),
  ...wireVehicleBusinessAttributes(state),
  ...wireVehicleUnderFinanceAttributes(state),
  ...wireVehiclePreExistingDamageAttributes(state),
  ...wireVehicleSpecialFeaturesAttributes(state),
];

export const manualToWireVehicle = (
  state: MotorFunnelState,
  vehicleDetails: VehicleManuallyEntered,
  coverageRefIds: string[]
): WireVehicleRequest => {
  const { make, model, year, nvic, code, bodyType, engine } = vehicleDetails;

  return {
    id: VEHICLE_ID,
    addressRefId: ADDRESS_ID,
    partyRefIds: vehicleRefPartyIds(state),
    year,
    make,
    model,
    type: bodyType,
    engine: engine.description,
    code,
    nvic,
    rego: {
      number:
        getLicencePlateNumberIfNotLookedUp(state).toUpperCase() || 'NOREG',
      providerType: VehicleProviderType.Internal,
    },
    coverageRefId: coverageRefIds,
    otherAttributes: wireVehicleOtherAttributes(state, vehicleDetails),
    modifications: wireVehicleModifications(state),
  };
};

export const lookupToWireVehicle = (
  state: MotorFunnelState,
  vehicle: VehicleFromSearch,
  coverageRefIds: string[]
): WireVehicleRequest => ({
  ...manualToWireVehicle(state, vehicle, coverageRefIds),
  rego: {
    number: vehicle.plateNumber.toUpperCase(),
    lookup: { success: 0, failure: 0 },
    providerType: VehicleProviderType.Motorweb,
  },
});

export const unmarshalVehicleValueRange = (quote: WireMotorQuotePayload) => ({
  lowerLimit: quote.vehicle.values.lowAgreed.toString(),
  upperLimit: quote.vehicle.values.highAgreed.toString(),
});

export const unmarshalVehicleMarketValue = (quote: WireMotorQuotePayload) =>
  quote.vehicle.values.market.toString();

export const unmarshalVehicleValue = (quote: WireMotorQuotePayload) =>
  quote.vehicle.values.selected === WireVehicleValueType.Market
    ? quote.vehicle.values.market.toString()
    : quote.vehicle.values.agreed.toString();

const toMarketAgreedValue = (
  wireVehicleValue: WireVehicleValueType
): MarketAgreedValue =>
  wireVehicleValue === WireVehicleValueType.Agreed
    ? MarketAgreedValue.Agreed
    : MarketAgreedValue.Market;

export const unmarshalMarketOrAgreedValue = (quote: WireMotorQuotePayload) =>
  toMarketAgreedValue(quote.vehicle.values.selected);

export const unmarshalVehicleModifications = (quote: WireMotorQuotePayload) =>
  quote.vehicle.modifications
    ?.filter((modification) => Boolean(modification.description))
    .map((modification) => ({
      name: modification.name,
      description: String(modification.description),
    })) || [];

const getModelCode = (vehicle: WireVehicleResponse): string =>
  vehicle.otherAttributes.find((attr) => attr.name === 'ModelCode')?.value ??
  '';

export const toIsUsedForBusiness = (
  quote: WireMotorQuotePayload
): YesNoAnswer =>
  quote.vehicle.otherAttributes.find(
    (attr) => attr.name === OtherAttributes.IS_USED_FOR_BUSINESS
  )?.value === 'true'
    ? YesNoAnswer.Yes
    : YesNoAnswer.No;

export const toBusinessType = (quote: WireMotorQuotePayload): string =>
  quote.vehicle.otherAttributes.find(
    (attr) => attr.name === OtherAttributes.IS_USED_FOR_BUSINESS
  )?.description || '';

export const toVehicleSearchType = (
  quote: WireMotorQuotePayload
): VehicleSearchType =>
  quote.vehicle.rego?.providerType === VehicleProviderType.Motorweb
    ? VehicleSearchType.RegoLookup
    : VehicleSearchType.ManualEntry;

export const toLicencePlate = (quote: WireMotorQuotePayload): string =>
  quote.vehicle.rego?.number || '';

export const toVehicleFromSearch = (
  quote: WireMotorQuotePayload
): VehicleFromSearch => {
  const {
    vehicle: { rego, type, year, make, engine, code, nvic, model },
  } = quote;

  return rego?.providerType === VehicleProviderType.Motorweb
    ? {
        bodyType: type,
        year,
        make,
        family: '',
        variant: '',
        driveType: '',
        engine: {
          description: engine,
          capacity: { value: '', unit: '' },
          cylinders: '',
        },
        transmissionType: '',
        plateNumber: rego.number,
        code,
        nvic,
        model,
        series: '',
        popular: false,
      }
    : defaultVehicleFromSearch;
};

export const toVehicleManualSelectFields = (
  quote: WireMotorQuotePayload
): Record<VehicleManualQuoteDetailsField, string> => {
  const { vehicle } = quote;
  const { rego, make, model, year, type, engine } = vehicle;

  return rego?.providerType === VehicleProviderType.Internal
    ? {
        vehicleMake: make.toUpperCase(),
        vehicleModel: `${model} ${getModelCode(vehicle)}`,
        vehicleManufactureYear: year,
        vehicleType: type,
        vehicleEngineType: engine,
      }
    : defaultVehicleManualSelectFields;
};

export const toVehicleManuallyEntered = (
  quote: WireMotorQuotePayload
): VehicleManuallyEntered => {
  const {
    vehicle: { rego, type, year, make, engine, code, nvic, model },
  } = quote;

  return rego?.providerType === VehicleProviderType.Internal
    ? {
        bodyType: type,
        year,
        make,
        family: '',
        variant: '',
        driveType: '',
        engine: {
          description: engine,
          capacity: { value: '', unit: '' },
          cylinders: '',
        },
        transmissionType: '',
        plateNumber: rego.number,
        code,
        nvic,
        model,
        series: '',
        popular: false,
      }
    : defaultVehicleFromSearch;
};
